<template>
  <button
    v-if="canHold"
    type="button"
    class="ex-act hold"
    :disabled="disabled"
    @click="emit('hold')"
  >
    {{ t('exclusiveUnits.action.hold') }}
  </button>
  <button
    v-else-if="holdLockReason === 'plan'"
    type="button"
    class="ex-act link"
    @click="openModal()"
  >
    {{ t('exclusiveUnits.action.plan') }}
  </button>
  <NuxtLink
    v-else-if="holdLockReason === 'uae_level'"
    :to="uaeStep.to"
    class="ex-act link"
  >
    {{ uaeStep.label }}
  </NuxtLink>
  <button
    v-else-if="holdLockReason === 'phone'"
    type="button"
    class="ex-act link"
    @click="emit('verify-phone')"
  >
    {{ t('exclusiveUnits.action.phone') }}
  </button>
  <span
    v-else
    class="ex-act text"
    >{{ t('exclusiveUnits.action.project') }}</span
  >
</template>

<script setup lang="ts">
import type { HoldLockReason } from '~/types/exclusives';
import { UAE_JOIN_ROUTE, UAE_VERIFY_ID_ROUTE } from '~/utils/uae-gate-client/uaeGate';

const {
  canHold,
  holdLockReason,
  uaeNextStep = null,
  disabled = false,
} = defineProps<{
  canHold: boolean;
  holdLockReason: HoldLockReason | null;
  uaeNextStep?: 'verify_id' | 'join' | null;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: 'hold'): void;
  (e: 'verify-phone'): void;
}>();

const { t } = useI18n();
// Plans modal for INT agents; UAE agents never reach pricing (openModal routes them to their next step).
const { openModal } = useBillingModal();

const uaeStep = computed(() =>
  uaeNextStep === 'join'
    ? { to: UAE_JOIN_ROUTE, label: t('exclusiveUnits.action.join') }
    : { to: UAE_VERIFY_ID_ROUTE, label: t('exclusiveUnits.action.verifyId') },
);
</script>

<style scoped lang="scss">
.ex-act {
  font-size: 12px;
  font-weight: 700;
  color: #0d0f15;
  white-space: normal;
  text-align: start;

  // Matches the Availability tab's gold "Book" button (100% Commission units).
  &.hold {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 7px 12px;
    border-radius: 8px;
    background: #ffb648;
    color: #0d0f15;
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 10px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    border: none;
    cursor: pointer;
    white-space: nowrap;

    &:hover:not(:disabled) {
      background: #eda436;
    }

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }

  &.link {
    background: none;
    border: none;
    padding: 0;
    color: #b07410;
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
  }

  &.text {
    font-weight: 500;
    color: rgba(13, 15, 21, 0.62);
  }
}
</style>
